import dayjs from 'dayjs';
import { computed, type MaybeRef, unref } from 'vue';
import { storeToRefs } from 'pinia';
import { useFirebaseStore } from 'freedom-shared';

export interface BlockedSearchBannerConfig {
  enabled: boolean;
  dep_iata_codes: string[];
  arr_iata_codes: string[];
  dep_blocked_until: string;
  arr_blocked_until: string;
  title_ru: string;
  title_kk: string;
  title_en: string;
  description_ru: string;
  description_kk: string;
  description_en: string;
}

export function useBlockedSearchBanner({
  depIata,
  arrIata,
  searchDate,
  returnDate,
}: {
  depIata: MaybeRef<string>;
  arrIata: MaybeRef<string>;
  searchDate: MaybeRef<string>;
  returnDate?: MaybeRef<string | undefined>;
}) {
  const { serializedRemoteConfig } = storeToRefs(useFirebaseStore());

  const config = computed<BlockedSearchBannerConfig | null>(
    () =>
      (serializedRemoteConfig.value
        ?.avfree_war_zone_banner as unknown as BlockedSearchBannerConfig) ??
      null,
  );

  const depIataCodes = computed(() => config.value?.dep_iata_codes ?? []);
  const arrIataCodes = computed(() => config.value?.arr_iata_codes ?? []);

  const isLegBlocked = (legDep: string, legArr: string, date: string) => {
    if (!config.value?.enabled) return false;
    const dep = legDep.substring(0, 3);
    const arr = legArr.substring(0, 3);

    const depBlocked =
      depIataCodes.value.includes(dep) &&
      !dayjs(date).isAfter(dayjs(config.value.dep_blocked_until), 'day');

    const arrBlocked =
      arrIataCodes.value.includes(arr) &&
      !dayjs(date).isAfter(dayjs(config.value.arr_blocked_until), 'day');

    return depBlocked || arrBlocked;
  };

  const isVisible = computed(() => {
    const dep = unref(depIata);
    const arr = unref(arrIata);

    if (isLegBlocked(dep, arr, unref(searchDate))) return true;

    const date2 = unref(returnDate);
    if (date2 && isLegBlocked(arr, dep, date2)) return true;

    return false;
  });

  return {
    isVisible,
    config,
  };
}
